import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { 
  ArrowLeftIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  PlusIcon,
  ChevronRightIcon,
  HomeIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'
import PoolAdminRoute from '../components/PoolAdminRoute'
import toast from 'react-hot-toast'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface EditablePool {
  poolId: string
  name: string
  description: string
  type: 'horse-racing'
  status: 'active' | 'completed' | 'cancelled'
  settings: {
    track: string
    raceDate: string
    pickDeadline: string
    entryFee: number
    maxParticipants: number
  }
  currentParticipants: number
  createdAt: string
  createdBy: string
  races: Race[]
}

interface Race {
  raceNumber: number
  postTime: string
  track: string
  raceType: string
  conditions: string
  purse: string
  distance?: string
  entries: Horse[]
  scratches?: string[]
  status: 'upcoming' | 'running' | 'final'
  results?: {
    winner?: string
    place?: string
    show?: string
  }
}

interface Horse {
  horse_id: string
  post_position: string
  post_position_color?: {
    background_color: string
    text_color: string
    name: string
  }
  horse_name: string
  jockey: string
  trainer: string
  weight: string
  medication?: string
  age_sex: string
}

export default function EditHorseRacingPoolPage() {
  const { poolId } = useParams<{ poolId: string }>()
  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()
  const [pool, setPool] = useState<EditablePool | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    track: '',
    raceDate: '',
    pickDeadline: '',
    entryFee: 0,
    maxParticipants: 0
  })

  useEffect(() => {
    if (poolId) {
      fetchPool()
    }
  }, [poolId])

  const fetchPool = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch pool: ${response.statusText}`)
      }

      const data = await response.json()
      const poolData = data.pool
      
      setPool(poolData)
      setFormData({
        name: poolData.name,
        description: poolData.description,
        track: poolData.settings.track,
        raceDate: poolData.settings.raceDate.split('T')[0], // Convert to YYYY-MM-DD
        pickDeadline: poolData.settings.pickDeadline.slice(0, 16), // Convert to datetime-local format
        entryFee: poolData.settings.entryFee,
        maxParticipants: poolData.settings.maxParticipants
      })
    } catch (err) {
      console.error('Error fetching pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch pool')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }))
  }

  const handleSave = async () => {
    if (!poolId) return

    try {
      setSaving(true)
      const token = await getAccessTokenSilently()

      // Convert form data back to API format
      const updateData = {
        name: formData.name,
        description: formData.description,
        settings: {
          track: formData.track,
          raceDate: formData.raceDate,
          pickDeadline: formData.pickDeadline,
          entryFee: formData.entryFee,
          maxParticipants: formData.maxParticipants
        }
      }

      const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      })

      if (!response.ok) {
        throw new Error(`Failed to update pool: ${response.statusText}`)
      }

      toast.success('Pool updated successfully!')
      navigate(`/admin/pools/horse-racing/${poolId}`)
    } catch (err) {
      console.error('Error updating pool:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to update pool')
    } finally {
      setSaving(false)
    }
  }

  const scratchHorse = async (raceNumber: number, horseId: string) => {
    if (!poolId) return

    try {
      const token = await getAccessTokenSilently()

      const response = await fetch(`${API_BASE_URL}/pools/${poolId}/scratch-horse`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ raceNumber, horseId })
      })

      if (!response.ok) {
        throw new Error(`Failed to scratch horse: ${response.statusText}`)
      }

      toast.success('Horse scratched successfully!')
      await fetchPool() // Refresh data
    } catch (err) {
      console.error('Error scratching horse:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to scratch horse')
    }
  }

  const unScratchHorse = async (raceNumber: number, horseId: string) => {
    if (!poolId) return

    try {
      const token = await getAccessTokenSilently()

      const response = await fetch(`${API_BASE_URL}/pools/${poolId}/unscratch-horse`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ raceNumber, horseId })
      })

      if (!response.ok) {
        throw new Error(`Failed to unscratch horse: ${response.statusText}`)
      }

      toast.success('Horse unscratched successfully!')
      await fetchPool() // Refresh data
    } catch (err) {
      console.error('Error unscratching horse:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to unscratch horse')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const canEdit = pool && pool.status === 'active' && pool.currentParticipants === 0

  if (loading) {
    return (
      <PoolAdminRoute>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </PoolAdminRoute>
    )
  }

  if (error) {
    return (
      <PoolAdminRoute>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
            <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
          </div>
        </div>
      </PoolAdminRoute>
    )
  }

  if (!pool) {
    return (
      <PoolAdminRoute>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Pool not found</h2>
            <p className="text-gray-600 dark:text-gray-300">The requested pool could not be found.</p>
          </div>
        </div>
      </PoolAdminRoute>
    )
  }

  return (
    <PoolAdminRoute>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link
                to="/pools"
                className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white"
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                Pools
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <Link
                  to="/admin/pools/horse-racing"
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2 dark:text-gray-400 dark:hover:text-white"
                >
                  Horse Racing Pools
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <Link
                  to={`/admin/pools/horse-racing/${poolId}`}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2 dark:text-gray-400 dark:hover:text-white"
                >
                  {pool.name}
                </Link>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2 dark:text-gray-400">
                  Edit
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Edit Pool - {pool.name}
              </h1>
              <p className="mt-1 text-lg text-gray-600 dark:text-gray-300">
                {pool.settings.track} • {formatDate(pool.settings.raceDate)}
              </p>
            </div>
            <Link
              to={`/admin/pools/horse-racing/${poolId}`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Pool
            </Link>
          </div>
        </div>

        {/* Edit Restrictions Warning */}
        {!canEdit && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-8">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Limited Editing Available
                </h3>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  {pool.currentParticipants > 0 
                    ? 'Pool settings cannot be changed once participants have joined. You can still manage scratches.'
                    : pool.status !== 'active'
                    ? 'Pool settings cannot be changed for inactive pools.'
                    : 'Pool editing is restricted.'
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Pool Settings */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pool Settings</h2>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pool Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={!canEdit}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    disabled={!canEdit}
                    rows={3}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Track
                    </label>
                    <input
                      type="text"
                      name="track"
                      value={formData.track}
                      onChange={handleInputChange}
                      disabled={!canEdit}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Race Date
                    </label>
                    <input
                      type="date"
                      name="raceDate"
                      value={formData.raceDate}
                      onChange={handleInputChange}
                      disabled={!canEdit}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pick Deadline
                  </label>
                  <input
                    type="datetime-local"
                    name="pickDeadline"
                    value={formData.pickDeadline}
                    onChange={handleInputChange}
                    disabled={!canEdit}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Entry Fee ($)
                    </label>
                    <input
                      type="number"
                      name="entryFee"
                      value={formData.entryFee}
                      onChange={handleInputChange}
                      disabled={!canEdit}
                      min="0"
                      step="0.01"
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Max Participants
                    </label>
                    <input
                      type="number"
                      name="maxParticipants"
                      value={formData.maxParticipants}
                      onChange={handleInputChange}
                      disabled={!canEdit}
                      min="1"
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                {canEdit && (
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Link
                      to={`/admin/pools/horse-racing/${poolId}`}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </Link>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="w-4 h-4 mr-2 inline-block" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pool Info */}
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pool Information</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">{pool.status}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Participants</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {pool.currentParticipants} / {pool.settings.maxParticipants}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Races</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{pool.races.length}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Created</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDate(pool.createdAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Race Management */}
        {pool.races.length > 0 && (
          <div className="mt-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Race Management</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Manage scratches and race entries
                </p>
              </div>
              <div className="p-6">
                <div className="space-y-6">
                  {pool.races.map((race) => (
                    <div key={race.raceNumber} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                            Race {race.raceNumber} - {race.raceType}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {race.postTime} • {race.distance} • ${parseInt(race.purse).toLocaleString()}
                          </p>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {race.entries.length} entries
                          {race.scratches && race.scratches.length > 0 && (
                            <span className="text-red-600 dark:text-red-400 ml-2">
                              • {race.scratches.length} scratched
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {race.entries.map((horse) => {
                          const isScratched = race.scratches?.includes(horse.horse_id)
                          return (
                            <div
                              key={horse.horse_id}
                              className={`p-3 rounded-lg border ${
                                isScratched
                                  ? 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800'
                                  : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  {horse.post_position_color ? (
                                    <div
                                      className="w-6 h-6 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-xs font-bold"
                                      style={{
                                        backgroundColor: horse.post_position_color.background_color,
                                        color: horse.post_position_color.text_color
                                      }}
                                    >
                                      {horse.post_position}
                                    </div>
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-bold">
                                      {horse.post_position}
                                    </div>
                                  )}
                                  <div>
                                    <div className={`text-sm font-medium ${
                                      isScratched 
                                        ? 'text-red-700 dark:text-red-300 line-through' 
                                        : 'text-gray-900 dark:text-white'
                                    }`}>
                                      {horse.horse_name}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      {horse.jockey}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => 
                                    isScratched 
                                      ? unScratchHorse(race.raceNumber, horse.horse_id)
                                      : scratchHorse(race.raceNumber, horse.horse_id)
                                  }
                                  className={`text-xs px-2 py-1 rounded ${
                                    isScratched
                                      ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/20 dark:text-green-400'
                                      : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400'
                                  }`}
                                >
                                  {isScratched ? 'Unscratch' : 'Scratch'}
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PoolAdminRoute>
  )
}
